import type { BusinessInfo } from '@/components/BusinessConfirmation'
import {
  BusinessUrlStep,
  CreateArticleStep,
  QuestionsStep,
  SuccessStep,
} from '@/components/onboarding/steps'
import {
  ensureBusinessInfoQueryData,
  invalidateBusinessInfoQuery,
} from '@/hooks/queries/useBusinessInfo'
import { setActiveBusinessId } from '@/hooks/useCurrentActiveBusiness'
import { useOnboardingSteps } from '@/hooks/useOnboardingSteps'
import { orpc } from '@/lib/orpc/client'
import type { onboardingQuestionsSchema } from '@repo/shared/schemas/onboarding'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AnimatePresence } from 'framer-motion'
import { type z } from 'zod'

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
})

function OnboardingPage() {
  const navigate = Route.useNavigate()
  const { currentStep, goToNextStep, goToPreviousStep } = useOnboardingSteps()

  const updateUserMutation = useMutation(
    orpc.onboarding.updateOnboardingInfo.mutationOptions(),
  )

  const createBusinessMutation = useMutation(
    orpc.business.createBusiness.mutationOptions({
      onSettled: async () => {
        await invalidateBusinessInfoQuery()
        await ensureBusinessInfoQueryData()
      },
      onSuccess: async (id) => {
        await setActiveBusinessId(id)
      },
    }),
  )

  const handleQuestionsSubmit = async (
    data: z.infer<typeof onboardingQuestionsSchema>,
  ) => {
    try {
      await updateUserMutation.mutateAsync({
        referralSource: data.referralSource,
      })
      goToNextStep()
    } catch (error) {
      console.error('Error updating user info:', error)
    }
  }

  const skipStep = () => {
    goToNextStep()
  }

  const handleBusinessSuccess = async (info: BusinessInfo) => {
    try {
      await createBusinessMutation.mutateAsync(info)
      goToNextStep()
    } catch (error) {
      console.error('Error creating business:', error)
    }
  }

  const handleFinish = () => {
    navigate({ to: '/', reloadDocument: true, replace: true })
    // Navigate to dashboard or home
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="pt-20 mx-auto max-w-2xl pb-20 px-4">
        <AnimatePresence mode="wait">
          {currentStep === 'questions' && (
            <QuestionsStep
              key="questions"
              onComplete={handleQuestionsSubmit}
              isLoading={updateUserMutation.isPending}
            />
          )}

          {currentStep === 'business-url' && (
            <BusinessUrlStep
              key="business-url"
              onComplete={handleBusinessSuccess}
              onBack={goToPreviousStep}
            />
          )}

          {currentStep === 'create-article' && (
            <CreateArticleStep
              key="create-article"
              onSkip={skipStep}
              onComplete={handleFinish}
            />
          )}

          {currentStep === 'success' && (
            <SuccessStep key="success" onFinish={handleFinish} />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
